import React, { useMemo } from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  ListItem,
} from '@/components/ui';
import { IconStack, IconX } from '@tabler/icons-react';

interface DuplicateFileControlsProps {
  hasDuplicates: boolean;
  duplicateFileNames: string[];
  duplicateFileStats: Record<string, number>;
  showDuplicatesOf?: string | null;
  onShowDuplicatesOf?: (fileName: string | null) => void;
  className?: string;
}

export const DuplicateFileControls: React.FC<DuplicateFileControlsProps> = ({
  hasDuplicates,
  duplicateFileNames,
  duplicateFileStats,
  showDuplicatesOf,
  onShowDuplicatesOf,
  className,
}) => {
  // Show duplicates dropdown
  const duplicateControls = useMemo(() => {
    if (!hasDuplicates || !onShowDuplicatesOf) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            variant="outlined"
            size="small"
            startIcon={<IconStack />}
            endIcon={
              <Badge
                variant="solid"
                color="error"
                size="xsmall"
                className="bulk-file-table__duplicate-count-badge"
              >
                {duplicateFileNames.length}
              </Badge>
            }
          >
            Show duplicates
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent variant="soft">
          {duplicateFileNames.map(fileName => (
            <DropdownMenuItem key={fileName} asChild>
              <ListItem
                primary={fileName}
                secondary={`${duplicateFileStats?.[fileName] || 0} duplicates`}
                onClick={() => onShowDuplicatesOf(fileName)}
                selected={showDuplicatesOf === fileName}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [
    hasDuplicates,
    duplicateFileNames,
    duplicateFileStats,
    showDuplicatesOf,
    onShowDuplicatesOf,
  ]);

  // Clear duplicates filter button
  const clearDuplicatesFilter =
    showDuplicatesOf && onShowDuplicatesOf ? (
      <Button
        variant="surface"
        color="error"
        size="small"
        startIcon={<IconX />}
        onClick={() => onShowDuplicatesOf(null)}
        ml="1"
        className="bulk-file-table__clear-duplicates-btn"
      >
        Show All Files
      </Button>
    ) : null;

  if (!duplicateControls && !clearDuplicatesFilter) {
    return null;
  }

  return (
    <Flex className={className}>
      {duplicateControls}
      {clearDuplicatesFilter}
    </Flex>
  );
};
